import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import {
  Instagram, ChevronRight, Bot, Smartphone, Laptop, Watch,
  Headphones, Tablet, ArrowRight, LogIn, Sparkles, MapPin
} from "lucide-react";
import { FaTiktok, FaWhatsapp, FaApple, FaGooglePlay, FaTelegramPlane } from "react-icons/fa";
import { TechArticleCardCompact } from "@/components/TechArticleCardCompact";
import { BottomNavigation } from "@/components/BottomNavigation";
import { ArticleDetail } from "@/components/ArticleDetail";
import { PageTransition } from "@/components/PageTransition";
import { MenuDrawer } from "@/components/MenuDrawer";
import Footer from "@/components/Footer";
import { useArticles, Article } from "@/hooks/useArticles";

import { useScrollReveal } from "@/hooks/useScrollReveal";
import MiniMapbox from "@/components/MiniMapbox";
import { motion, useScroll, useTransform } from "framer-motion";
import TeclyPullRefresh from "@/components/TeclyPullRefresh";
import heroBg from "@/assets/hero-bg.jpg";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  smartphone: <Smartphone className="w-7 h-7" />,
  laptop: <Laptop className="w-7 h-7" />,
  watch: <Watch className="w-7 h-7" />,
  headphones: <Headphones className="w-7 h-7" />,
  tablet: <Tablet className="w-7 h-7" />,
};

/* ─── Scroll-reveal section wrapper ─── */
const RevealSection = ({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};


const Index = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const navigate = useNavigate();

  const { articles, userLikes, loading, recordView, toggleLike, refetch } = useArticles(user);


  // Parallax for hero
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  // Pull-to-refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const PULL_THRESHOLD = 80;

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);


  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) setPullDistance(Math.min(diff * 0.5, 120));
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) handleRefresh();
    setPullDistance(0);
    isPulling.current = false;
  }, [pullDistance, isRefreshing, handleRefresh]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (selectedArticle) {
      window.history.pushState({ articleDetail: true }, '');
      const h = () => setSelectedArticle(null);
      window.addEventListener('popstate', h);
      return () => window.removeEventListener('popstate', h);
    }
  }, [selectedArticle]);

  const handleArticleClick = (article: Article) => { setSelectedArticle(article); recordView(article.id); };
  const handleLike = (articleId: string) => toggleLike(articleId);

  const latestArticles = articles.slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }


  return (
    <PageTransition>
      <div
        ref={scrollRef}
        className="min-h-screen bg-background pb-32 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull to refresh */}
        <TeclyPullRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />

        {/* ═══════════════ HERO SECTION ═══════════════ */}
        <section ref={heroRef} className="relative overflow-hidden">
          <motion.div
            className="absolute inset-0 z-0"
            style={{ y: heroY }}
          >
            <img
              src={heroBg}
              alt=""
              className="w-full h-full object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-background" />
          </motion.div>

          <motion.div
            className="relative z-10 max-w-6xl mx-auto px-6 pb-20 text-center"
            style={{
              opacity: heroOpacity,
              paddingTop: "calc(4rem + env(safe-area-inset-top, 0px))",
            }}
          >
            {/* Header inside hero */}
            <nav className="flex flex-col items-center gap-4 mb-12 md:flex-row md:justify-between md:items-center">
              <h1 className="text-3xl font-bold text-white">
                Tecly
              </h1>
              <div className="flex items-center gap-1.5">
                {!user && (
                  <>
                    <button
                      onClick={() => navigate("/auth")}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-all text-xs font-medium text-white"
                    >
                      <LogIn className="w-3.5 h-3.5" />
                      Accedi
                    </button>
                    <button
                      onClick={() => navigate("/auth?tab=register")}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary hover:bg-primary/90 transition-all text-xs font-medium text-primary-foreground"
                    >
                      Registrati
                    </button>
                    <div className="w-px h-4 bg-white/20 mx-1" />
                  </>
                )}
                <a href="https://www.instagram.com/tecly.it?igsh=MXR1bmQ0bGdtbjBsNw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-white/10 transition-all group">
                  <Instagram className="w-5 h-5 text-white/70 group-hover:text-white transition-colors" />
                </a>
                <a href="https://www.tiktok.com/@tecly6?_r=1&_t=ZN-92lek6T8jry" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-white/10 transition-all group">
                  <FaTiktok className="w-5 h-5 text-white/70 group-hover:text-white transition-colors" />
                </a>
                <a href="https://t.me/Teclynet" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-white/10 transition-all group">
                  <FaTelegramPlane className="w-5 h-5 text-white/70 group-hover:text-white transition-colors" />
                </a>
                <a href="https://whatsapp.com/channel/0029VbCTNmGICVft1h5A2z2u" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-white/10 transition-all group">
                  <FaWhatsapp className="w-5 h-5 text-white/70 group-hover:text-white transition-colors" />
                </a>
              </div>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h2 className="text-[2rem] md:text-6xl font-bold text-white mb-4 leading-tight">
                Il tuo mondo<br />
                <span className="bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">
                  Apple, sempre con te.
                </span>
              </h2>
              <p className="text-base text-white/70 max-w-md mx-auto mb-8">
                News, sfondi, confronti iPhone e l'assistente Tecly AI. Tutto in un'unica app.
              </p>

            </motion.div>

            <motion.div
              className="flex flex-row items-center justify-center gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <button
                onClick={() => navigate("/install")}
                className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all active:scale-95 border border-white/20"
              >
                <FaApple className="w-6 h-6" />
                <span className="text-sm font-semibold">iOS</span>
              </button>
              <button
                onClick={() => navigate("/install")}
                className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all active:scale-95 border border-white/20"
              >
                <FaGooglePlay className="w-5 h-5" />
                <span className="text-sm font-semibold">Android</span>
              </button>
            </motion.div>
          </motion.div>
        </section>




        {/* ═══════════════ TECLY AI CTA (compact) ═══════════════ */}
        <RevealSection className="max-w-6xl mx-auto px-6 mt-10" delay={100}>
          <motion.div
            onClick={() => navigate("/tecly-ai")}
            className="relative overflow-hidden rounded-3xl cursor-pointer group flex items-center gap-4 px-5 py-4"
            style={{
              background: "linear-gradient(135deg, #3B82F6, #06B6D4)",
              boxShadow: "0 8px 32px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
            }}
            whileTap={{ scale: 0.97 }}
          >
            <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-lg font-bold text-white flex-1">Tecly AI</h2>
            <ArrowRight className="w-5 h-5 text-white/80 group-hover:translate-x-1 transition-transform" />
          </motion.div>
        </RevealSection>

        {/* ═══════════════ LATEST NEWS ═══════════════ */}
        {latestArticles.length > 0 && (
          <RevealSection className="max-w-6xl mx-auto px-6 mt-10" delay={100}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-2xl font-bold text-foreground">Ultime News</h2>
              <button onClick={() => navigate("/news")} className="flex items-center gap-1 text-sm text-primary font-medium hover:underline">
                Tutte le news <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {latestArticles.map((article) => (
                <TechArticleCardCompact
                  key={article.id}
                  image={article.image}
                  title={article.title}
                  description={article.description}
                  views={article.views_count}
                  liked={userLikes.has(article.id)}
                  likes={article.likes_count}
                  onLike={() => handleLike(article.id)}
                  onClick={() => handleArticleClick(article)}
                />
              ))}
            </div>
          </RevealSection>
        )}

        {/* ═══════════════ CONFRONTA IPHONE ═══════════════ */}
        <RevealSection className="max-w-6xl mx-auto px-6 mt-10" delay={100}>
          <motion.div
            onClick={() => navigate("/confronta-iphone")}
            className="relative overflow-hidden rounded-3xl p-8 cursor-pointer group"
            whileTap={{ scale: 0.98 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 opacity-90" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(255,255,255,0.08)_0%,_transparent_60%)]" />
            <div className="relative z-10 flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                <Smartphone className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-1">Confronta iPhone</h2>
                <p className="text-sm text-white/70">
                  Confronta le specifiche tecniche di tutti i modelli iPhone dal 2007 ad oggi. Scegli fino a 3 modelli.
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
            </div>
          </motion.div>
        </RevealSection>

        {/* ═══════════════ STORE MAP ═══════════════ */}
        <RevealSection className="max-w-6xl mx-auto px-6 mt-10" delay={100}>
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Apple Store nelle vicinanze</h2>
          </div>
          <div className="rounded-2xl overflow-hidden border border-border/50 shadow-sm">
            <MiniMapbox />
          </div>
        </RevealSection>

        {/* ═══════════════ CTA FINALE ═══════════════ */}
        {!user && (
          <RevealSection className="max-w-6xl mx-auto px-6 mt-12 mb-4" delay={100}>
            <div className="text-center p-8 rounded-3xl bg-card border border-border/50">
              <h2 className="text-2xl font-bold text-foreground mb-2">Pronto a iniziare?</h2>
              <p className="text-muted-foreground mb-6 text-sm">
                Registrati gratuitamente per accedere a tutte le funzionalità, offerte esclusive e molto altro.
              </p>
              <button
                onClick={() => navigate("/auth")}
                className="px-8 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all active:scale-95 shadow-lg shadow-primary/25"
              >
                Crea un account gratuito
              </button>
            </div>
          </RevealSection>
        )}

        {/* ═══════════════ FOOTER ═══════════════ */}
        <Footer user={user} />

        {user && (
          <>
            <BottomNavigation
              onMenuClick={() => setIsMenuOpen((v) => !v)}
              menuOpen={isMenuOpen}
              activeTab="home"
            />
            <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
          </>
        )}

        {/* Article Detail */}
        {selectedArticle && (
          <ArticleDetail
            image={selectedArticle.image}
            title={selectedArticle.title}
            description={selectedArticle.description}
            views={selectedArticle.views_count}
            liked={userLikes.has(selectedArticle.id)}
            content={selectedArticle.content as { type: "text" | "image"; content: string }[] | undefined}
            articleId={selectedArticle.id}
            onLike={() => handleLike(selectedArticle.id)}
            onClose={() => window.history.back()}
          />
        )}

      </div>
    </PageTransition>
  );
};

export default Index;
